// packages import
import { createContext, FC, Reducer, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'

// others
import { BillingType, BillStatus, useFetchPreBillsLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY } from '../constants'
import { ChildrenType } from '../interfaceTypes'
import {
  PreBillsAction,
  PreBillsActionType,
  preBillsInitialState,
  preBillsReducer,
  PreBillsState,
  PreBillsFilters,
  PreBillsSortBy,
  PreBillsSortDir
} from '../components/billing/preBills/preBillsReducer'
import { AuthContext } from './AuthContext'
import { AppContext } from './AppContext'
import { ActionType } from './AppContextReducer'
import { useSearchParams } from 'react-router-dom'

const PAGE_LIMIT = 20
export interface PreBillsContextType extends PreBillsState {
  dispatch: React.Dispatch<PreBillsAction>
  filters: PreBillsFilters
  refetch: () => void
  setFilters: (filters: PreBillsFilters) => void
}

export const PreBillsContext = createContext<PreBillsContextType>({
  ...preBillsInitialState,
  filters: {
    searchTerm: '',
    sortBy: PreBillsSortBy.Date,
    sortDir: PreBillsSortDir.Desc
  },
  dispatch: () => {
    return
  },
  refetch: () => {
    return
  },
  setFilters: () => {
    return
  }
})

export const PreBillsContextProvider: FC<ChildrenType> = ({ children }): JSX.Element => {
  const { isLoggedIn } = useContext(AuthContext)
  const { dispatch: appDispatch } = useContext(AppContext)
  const [state, dispatch] = useReducer<Reducer<PreBillsState, PreBillsAction>>(preBillsReducer, preBillsInitialState)
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo<PreBillsFilters>(() => {
    const attorneyId = searchParams.get('attorneyId') || undefined
    const lawTypeId = searchParams.get('lawTypeId') || undefined
    const caseType = (searchParams.get('caseType') as BillingType) || undefined
    const status = (searchParams.get('status') as BillStatus) || undefined
    const searchTerm = searchParams.get('search') || ''
    const billDeliveryType = searchParams.get('billDeliveryType') || undefined
    const sortBy = (searchParams.get('sortBy') as PreBillsSortBy) || PreBillsSortBy.MatterId
    const sortDir = (searchParams.get('sortDir') as PreBillsSortDir) || PreBillsSortDir.Desc
    const onlyWithTrust = searchParams.get('onlyWithTrust') === 'true'
    const onlyWithCredit = searchParams.get('onlyWithCredit') === 'true'

    return {
      attorneyId,
      lawTypeId,
      caseType,
      status,
      searchTerm,
      billDeliveryType,
      sortBy,
      sortDir,
      onlyWithTrust,
      onlyWithCredit
    }
  }, [searchParams])

  const currentPage = useMemo(() => parseInt(searchParams.get('currentPage') || '1'), [searchParams])

  const [fetchPreBillsQuery, { loading, refetch }] = useFetchPreBillsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {
      dispatch({ type: PreBillsActionType.SET_PRE_BILLS, preBills: [] })
      dispatch({ type: PreBillsActionType.SET_LOADING, loading: false })
      appDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    },
    onCompleted(data) {
      if (data?.fetchPreBills) {
        const { preBills, pagination } = data.fetchPreBills

        if (preBills) {
          dispatch({ type: PreBillsActionType.SET_PRE_BILLS, preBills: preBills || [] })
        }

        if (pagination) {
          const { totalPages, totalCount } = pagination
          totalPages && appDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
          totalCount && appDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
        }

        dispatch({ type: PreBillsActionType.SET_LOADING, loading: false })
        appDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      }
    }
  })

  useEffect(() => {
    if (isLoggedIn) {
      dispatch({ type: PreBillsActionType.SET_LOADING, loading: true })

      fetchPreBillsQuery({
        variables: {
          fetchPreBillsInput: {
            searchTerm: filters.searchTerm,
            page: currentPage,
            limit: PAGE_LIMIT,
            attorneyId: filters.attorneyId,
            lawTypeId: filters.lawTypeId,
            caseType: filters.caseType,
            status: filters.status,
            billDeliveryType: filters.billDeliveryType,
            sortBy: filters.sortBy,
            sortDir: filters.sortDir,
            onlyWithTrust: filters.onlyWithTrust,
            onlyWithCredit: filters.onlyWithCredit
          }
        }
      })
    }
  }, [filters, currentPage, isLoggedIn, appDispatch, fetchPreBillsQuery])

  useEffect(() => {
    // Set current page from query params
    appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage })

    return () => {
      appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
    // eslint-disable-next-line
  }, [])

  const setFilters = useCallback(
    (newFilters: PreBillsFilters) => {
      setSearchParams(params => {
        if ('searchTerm' in newFilters) {
          if (newFilters.searchTerm) {
            params.set('search', newFilters.searchTerm)
          } else {
            params.delete('search')
          }
        }

        if ('attorneyId' in newFilters) {
          if (newFilters.attorneyId) {
            params.set('attorneyId', newFilters.attorneyId)
          } else {
            params.delete('attorneyId')
          }
        }

        if ('lawTypeId' in newFilters) {
          if (newFilters.lawTypeId) {
            params.set('lawTypeId', newFilters.lawTypeId)
          } else {
            params.delete('lawTypeId')
          }
        }

        if ('caseType' in newFilters) {
          if (newFilters.caseType) {
            params.set('caseType', newFilters.caseType)
          } else {
            params.delete('caseType')
          }
        }

        if ('status' in newFilters) {
          if (newFilters.status) {
            params.set('status', newFilters.status)
          } else {
            params.delete('status')
          }
        }

        if ('billDeliveryType' in newFilters) {
          if (newFilters.billDeliveryType) {
            params.set('billDeliveryType', newFilters.billDeliveryType)
          } else {
            params.delete('billDeliveryType')
          }
        }

        if ('sortBy' in newFilters) {
          if (newFilters.sortBy) {
            params.set('sortBy', newFilters.sortBy)
          } else {
            params.delete('sortBy')
          }
        }

        if ('sortDir' in newFilters) {
          if (newFilters.sortDir) {
            params.set('sortDir', newFilters.sortDir)
          } else {
            params.delete('sortDir')
          }
        }

        if ('onlyWithTrust' in newFilters) {
          if (newFilters.onlyWithTrust) {
            params.set('onlyWithTrust', 'true')
          } else {
            params.delete('onlyWithTrust')
          }
        }

        if ('onlyWithCredit' in newFilters) {
          if (newFilters.onlyWithCredit) {
            params.set('onlyWithCredit', 'true')
          } else {
            params.delete('onlyWithCredit')
          }
        }

        // Reset page to 1 when filters change
        params.set('currentPage', '1')
        appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })

        return params
      })
    },
    [setSearchParams, appDispatch]
  )

  const contextValue = useMemo(
    () => ({
      ...state,
      filters,
      loading: loading || state.loading,
      dispatch,
      refetch,
      setFilters
    }),
    [state, loading, refetch, setFilters, filters]
  )

  return <PreBillsContext.Provider value={contextValue}>{children}</PreBillsContext.Provider>
}
